import { Schema } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import type { StaffRole } from "../../../packages/common/src/schemas/auth.ts";
import { activeStates, orderStateSchema } from "#orders.ts";

const statusSchema = Schema.Literal(
  "active",
  ...orderStateSchema.literals,
  "all",
);
const tabSchema = Schema.Literal("live", "scheduled");
const idSchema = Schema.NonEmptyString.pipe(Schema.maxLength(255));

/** Applied kitchen/history view; transient filter drafts stay outside the URL. */
export type OrderView = {
  readonly status: typeof statusSchema.Type;
  readonly locationIds: readonly string[];
  readonly deviceIds: readonly string[];
  readonly tab: typeof tabSchema.Type;
};

/** History remains an administrator-only choice, including on direct links. */
export const orderStatusFilters = (
  role: StaffRole,
): readonly OrderView["status"][] =>
  statusSchema.literals.filter(
    (status) =>
      role === "admin" ||
      status === "active" ||
      (status !== "all" && activeStates.includes(status)),
  );

function selectedIds(params: URLSearchParams, key: string): readonly string[] {
  return [
    ...new Set(
      params
        .getAll(key)
        .slice(0, 300)
        .flatMap((value) => {
          const id = decodeUnknownOption(idSchema, value.trim());
          return id ? [id] : [];
        }),
    ),
  ];
}

/** Parse shareable views independently of loaded orders or cached location names. */
export function readOrderView(
  params: URLSearchParams,
  role: StaffRole,
): OrderView {
  const status =
    decodeUnknownOption(statusSchema, params.get("status")) ?? "active";
  return {
    status: orderStatusFilters(role).includes(status) ? status : "active",
    locationIds: selectedIds(params, "location"),
    deviceIds: selectedIds(params, "device"),
    tab: decodeUnknownOption(tabSchema, params.get("tab")) ?? "live",
  };
}

/** Commit all filters together, omitting defaults and retaining unrelated parameters. */
export function writeOrderView(
  current: URLSearchParams,
  view: OrderView,
): URLSearchParams {
  const params = new URLSearchParams(current);
  for (const key of ["status", "location", "device", "tab"]) params.delete(key);
  if (view.status !== "active") params.set("status", view.status);
  for (const id of new Set(view.locationIds)) params.append("location", id);
  for (const id of new Set(view.deviceIds)) params.append("device", id);
  if (view.tab !== "live") params.set("tab", view.tab);
  return params;
}

/** Supported preorder filters, matching the existing API states. */
export const preorderStatusSchema = Schema.Literal(
  "Confirmed",
  "Completed",
  "Cancelled",
  "RecoveryRequired",
  "All",
);
const pickupDateSchema = Schema.String.pipe(
  Schema.pattern(/^\d{4}-\d{2}-\d{2}$/),
  Schema.filter((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return (
      !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
    );
  }),
);

/** Applied preorder status and inclusive pickup-date bounds. */
export type PreorderFilters = {
  readonly status: typeof preorderStatusSchema.Type;
  readonly pickupFrom: string;
  readonly pickupTo: string;
};

/** An unfiltered pickup range showing confirmed preorders. */
export const defaultPreorderFilters: PreorderFilters = {
  status: "Confirmed",
  pickupFrom: "",
  pickupTo: "",
};

/** Ignore malformed calendar dates and discard an inverted range as a pair. */
export function readPreorderFilters(params: URLSearchParams): PreorderFilters {
  const pickupFrom =
    decodeUnknownOption(pickupDateSchema, params.get("pickupFrom")) ?? "";
  const pickupTo =
    decodeUnknownOption(pickupDateSchema, params.get("pickupTo")) ?? "";
  const inverted = Boolean(pickupFrom && pickupTo && pickupFrom > pickupTo);
  return {
    status:
      decodeUnknownOption(preorderStatusSchema, params.get("status")) ??
      "Confirmed",
    pickupFrom: inverted ? "" : pickupFrom,
    pickupTo: inverted ? "" : pickupTo,
  };
}

/** Update preorder fields in a single navigation without dropping unrelated parameters. */
export function writePreorderFilters(
  current: URLSearchParams,
  filters: PreorderFilters,
): URLSearchParams {
  const params = new URLSearchParams(current);
  for (const key of ["status", "pickupFrom", "pickupTo"]) params.delete(key);
  if (filters.status !== "Confirmed") params.set("status", filters.status);
  if (filters.pickupFrom) params.set("pickupFrom", filters.pickupFrom);
  if (filters.pickupTo) params.set("pickupTo", filters.pickupTo);
  return params;
}
